import { supabase } from '../supabase';
import { checkRateLimit } from '../utils/rateLimiter';
import { getRadcoonSessionToken } from './auth';

const DEFAULT_CONSTANTS = {
  MAX_RIGS: 4,
  RIG_TIER_POWERS: [10, 15, 25, 40],
  RIG_TIER_COSTS: [10, 20, 40, 80],
  RIG_TIER_EFFICIENCY: [1, 1.2, 1.5, 2],
  DAILY_TOKEN_POOL: 40,
  REWARD_TOKEN_SYMBOL: 'DCKSLAP',
  REWARD_TOKEN_DISPLAY: '$DCKSLAP',
  MINING_REQUIREMENT_TOKEN_SYMBOL: '$REDDICKS',
  MINING_INTERVALS_PER_DAY: 288,
  MINING_INTERVAL_MINUTES: 5,
  REWARD_DIVISOR: 50,
  PASSIVE_INCOME_FREQUENCY: 1,
  MAX_OFFLINE_HOURS: 168,
  MONEY_TOLERANCE: 1,
  MIN_MONEY: 0,
  MINIMUM_WITHDRAWAL_AMOUNT: 20,
  MIN_WALLET_BALANCE_FOR_MINING: 15000,
  STARTING_BALANCE: 10,
  MAX_SAVES_PER_USER: 1,
  TOTAL_MINING_POWER: null,
};

export function getGameConstants() {
  return { ...DEFAULT_CONSTANTS };
}

const constantsFromEdge = (edgeConstants) => {
  if (!edgeConstants) return getGameConstants();

  return {
    ...getGameConstants(),
    MAX_RIGS: edgeConstants.maxRigs ?? DEFAULT_CONSTANTS.MAX_RIGS,
    RIG_TIER_POWERS: [
      edgeConstants.rigPowers?.[1] ?? DEFAULT_CONSTANTS.RIG_TIER_POWERS[0],
      edgeConstants.rigPowers?.[2] ?? DEFAULT_CONSTANTS.RIG_TIER_POWERS[1],
      edgeConstants.rigPowers?.[3] ?? DEFAULT_CONSTANTS.RIG_TIER_POWERS[2],
      edgeConstants.rigPowers?.[4] ?? DEFAULT_CONSTANTS.RIG_TIER_POWERS[3],
    ],
    RIG_TIER_COSTS: [
      edgeConstants.rigCosts?.[1] ?? DEFAULT_CONSTANTS.RIG_TIER_COSTS[0],
      edgeConstants.rigCosts?.[2] ?? DEFAULT_CONSTANTS.RIG_TIER_COSTS[1],
      edgeConstants.rigCosts?.[3] ?? DEFAULT_CONSTANTS.RIG_TIER_COSTS[2],
      edgeConstants.rigCosts?.[4] ?? DEFAULT_CONSTANTS.RIG_TIER_COSTS[3],
    ],
    MINIMUM_WITHDRAWAL_AMOUNT:
      edgeConstants.minimumWithdrawalAmount ?? DEFAULT_CONSTANTS.MINIMUM_WITHDRAWAL_AMOUNT,
    MIN_WALLET_BALANCE_FOR_MINING:
      edgeConstants.requiredReddicksBalance ?? DEFAULT_CONSTANTS.MIN_WALLET_BALANCE_FOR_MINING,
    STARTING_BALANCE: edgeConstants.startingBalance ?? DEFAULT_CONSTANTS.STARTING_BALANCE,
    TOTAL_MINING_POWER:
      edgeConstants.totalMiningPower ?? DEFAULT_CONSTANTS.TOTAL_MINING_POWER ?? null,
  };
};

const toError = (error) => {
  if (!error) return null;
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  return new Error(error.message || 'Request failed');
};

const invokeRadcoonGame = async (body, sessionToken = null) => {
  const token = sessionToken || getRadcoonSessionToken();

  if (!token) {
    return { error: new Error('No active Radcoon session. Reconnect your wallet.') };
  }

  const { data, error } = await supabase.functions.invoke('radcoon-game', {
    body,
    headers: { 'x-radcoon-session': token },
  });

  if (error) {
    return { error: toError(error) };
  }

  if (data?.success === false) {
    return { error: new Error(data.error || 'Game request failed') };
  }

  return {
    ...data,
    constants: constantsFromEdge(data?.constants),
  };
};

export async function getUserSave(userId, sessionToken = null) {
  if (!userId) return { error: new Error('No user ID provided') };

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      { action: 'getSave' },
      sessionToken
    );

    if (error) return { error };

    return {
      save: save || null,
      constants: constants || getGameConstants(),
    };
  } catch (error) {
    console.error('Unexpected error fetching user save:', error);
    return { error };
  }
}

export async function createSave(userId, companyName, initialData = {}, sessionToken = null) {
  if (!userId) return { error: new Error('No user ID provided') };
  if (!companyName) return { error: new Error('No company name provided') };

  const rateLimitResult = checkRateLimit(userId, 'save_creation');
  if (!rateLimitResult.allowed) {
    return {
      error: {
        message: `Rate limit exceeded. Please wait ${rateLimitResult.retryAfter} seconds before trying again.`,
        rateLimit: true,
        retryAfter: rateLimitResult.retryAfter,
      },
      success: false,
    };
  }

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      {
        action: 'getOrCreateSave',
        companyName,
        initialData,
      },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  } catch (error) {
    console.error('Unexpected error creating save:', error);
    return { error };
  }
}

export async function updateSave(saveId, gameData, sessionToken = null) {
  if (!saveId) return { error: new Error('No save ID provided') };
  if (!gameData) return { error: new Error('No game data provided') };

  const rigs = gameData?.data?.miningRigs || gameData?.miningRigs;
  if (!Array.isArray(rigs)) {
    const { save, constants, error } = await invokeRadcoonGame(
      { action: 'getSave' },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  }

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      {
        action: 'syncRigState',
        saveId,
        rigs,
      },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  } catch (error) {
    console.error('Unexpected error updating game data:', error);
    return { error };
  }
}

export async function processMiningIncome(saveId, hoursElapsed = 0, sessionToken = null) {
  if (!saveId) return { error: new Error('No save ID provided') };
  if (typeof hoursElapsed !== 'number' || hoursElapsed < 0) {
    return { error: new Error('Invalid hours elapsed value') };
  }

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      { action: 'getSave' },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      income: 0,
      miningPower: save?.mining_power || 0,
      hourlyRate: 0,
      message: 'Passive income is processed by the scheduled DCKSLAP payout job.',
      constants,
    };
  } catch (error) {
    console.error('Unexpected error processing income:', error);
    return { error };
  }
}

export async function updateMiningRigs(saveId, rigs, money, miningPower, sessionToken = null) {
  if (!saveId) return { error: new Error('No save ID provided') };
  if (!Array.isArray(rigs)) return { error: new Error('Invalid rig data format') };
  if (typeof money !== 'number') return { error: new Error('Invalid money value') };
  if (money < 0) return { error: new Error('Money cannot be negative') };

  const rateLimitResult = checkRateLimit(saveId, 'mining_rig_purchase');
  if (!rateLimitResult.allowed) {
    return {
      error: {
        message: `Please wait before making another purchase (${rateLimitResult.retryAfter}s)`,
        rateLimit: true,
        retryAfter: rateLimitResult.retryAfter,
      },
      success: false,
    };
  }

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      {
        action: 'purchaseRig',
        saveId,
        rigs,
        expectedMoney: money,
        expectedMiningPower: miningPower,
      },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  } catch (error) {
    console.error('Unexpected error updating mining rigs:', error);
    return { error };
  }
}

export async function updateRigStatus(saveId, rigs, sessionToken = null) {
  if (!saveId) return { error: new Error('No save ID provided') };
  if (!Array.isArray(rigs)) return { error: new Error('Invalid rig data format') };

  try {
    const { save, constants, error } = await invokeRadcoonGame(
      {
        action: 'syncRigState',
        saveId,
        rigs,
      },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  } catch (error) {
    console.error('Unexpected error updating rig status:', error);
    return { error };
  }
}

export async function useOneGeneration(sessionToken = null) {
  try {
    const { save, constants, error } = await invokeRadcoonGame(
      { action: 'useGeneration' },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
    };
  } catch (error) {
    console.error('Unexpected error using generation:', error);
    return { error };
  }
}

export async function recordWithdrawal(saveId, withdrawnAmount, txId = null, walletAddress = null, sessionToken = null) {
  if (!saveId) return { error: new Error('No save ID provided') };
  if (typeof withdrawnAmount !== 'number' || withdrawnAmount <= 0) {
    return { error: new Error('Invalid withdrawal amount') };
  }

  try {
    const { save, constants, transactionId, withdrawal, error } = await invokeRadcoonGame(
      {
        action: 'withdrawDckslap',
        saveId,
        clientTransactionId: txId,
        walletAddress,
      },
      sessionToken
    );

    if (error) return { error };

    return {
      success: true,
      save,
      constants,
      withdrawnAmount: withdrawal?.amount ?? withdrawnAmount,
      txId: transactionId || txId,
      transactionId: transactionId || txId,
    };
  } catch (error) {
    console.error('Unexpected error recording withdrawal:', error);
    return { error };
  }
}
